import React, { useState, useEffect } from 'react';
import api from '../../api';
import toast from 'react-hot-toast';

// TypeScript Interfaces
interface DiffResult {
  success: boolean;
  process_map: {
    id: number;
    process_name: string;
    level: number;
    total_steps: number;
  };
  sop_info: {
    total_sops: number;
    sops: Array<{ id: number; title: string }>;
    linked_sections: number;
    total_mappings: number;
  };
  diff_summary: {
    total_changes: number;
    added_steps: number;
    modified_steps: number;
    deleted_steps: number;
    unchanged_steps: number;
  };
  changes: ChangeItem[];
  recommendations: string[];
}

interface ChangeItem {
  type: 'added' | 'modified' | 'deleted' | 'unchanged';
  step: {
    id: number;
    step_number: string;
    step_title: string;
    step_description?: string;
    modified_at: string;
    modified_by_name?: string;
    validation_status?: string;
  } | null;
  sop_mapping: {
    id?: number;
    section_number: string;
    section_title: string;
    last_synced_at: string;
    sop_id?: number;
    sop_title?: string;
  } | null;
  diff_details?: {
    title_changed: boolean;
    description_changed: boolean;
    step_modified_after_sync: boolean;
  };
  sync_status: string;
  reason?: string;
}

interface ProcessMapDiffProps {
  processMapId: number;
}

const ProcessMapDiff: React.FC<ProcessMapDiffProps> = ({ processMapId }) => {
  const [diffData, setDiffData] = useState<DiffResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [showAllChanges, setShowAllChanges] = useState(false);
  const [showUnchanged, setShowUnchanged] = useState(false);

  useEffect(() => {
    if (processMapId) {
      fetchDiffData();
    }
  }, [processMapId]);

  const fetchDiffData = async () => {
    setLoading(true);
    try {
      console.log('🔍 [ProcessMapDiff] Fetching diff for process map:', processMapId);
      const response = await api.get(`/api/analysis/process-maps/${processMapId}/diff`);
      console.log('✅ [ProcessMapDiff] Diff data loaded:', response.data);
      setDiffData(response.data);
    } catch (error) {
      console.error('❌ [ProcessMapDiff] Failed to fetch diff:', error);
      toast.error('Failed to load diff data');
    } finally {
      setLoading(false);
    }
  };

  const getChangeIcon = (type: string) => {
    switch (type) {
      case 'added': return '🟢';
      case 'modified': return '🟡';
      case 'deleted': return '🔴';
      case 'unchanged': return '✅';
      default: return '⚪';
    }
  };

  const getChangeBgColor = (type: string) => {
    switch (type) {
      case 'added': return 'bg-green-50 border-green-200';
      case 'modified': return 'bg-yellow-50 border-yellow-200';
      case 'deleted': return 'bg-red-50 border-red-200';
      case 'unchanged': return 'bg-gray-50 border-gray-200';
      default: return 'bg-white border-gray-200';
    }
  };

  const getChangeTypeLabel = (type: string) => {
    switch (type) {
      case 'added': return 'Added Steps';
      case 'modified': return 'Modified Steps';
      case 'deleted': return 'Deleted Steps';
      case 'unchanged': return 'Unchanged Steps';
      default: return 'Unknown';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  const renderDiffSummary = () => {
    if (!diffData) return null;

    return (
      <div className="bg-blue-50 rounded-lg p-4 border border-blue-200 mb-4">
        <h4 className="font-semibold text-blue-900 mb-3 flex items-center">
          <span className="mr-2">📊</span>
          Diff Summary
        </h4>
        <div className="space-y-2 text-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <span className="text-green-600 font-bold text-lg">➕ {diffData.diff_summary.added_steps}</span>
              <span className="text-gray-700">Added Steps</span>
            </div>
            <span className="text-xs text-gray-500">(need SOP sections)</span>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <span className="text-yellow-600 font-bold text-lg">✏️ {diffData.diff_summary.modified_steps}</span>
              <span className="text-gray-700">Modified Steps</span>
            </div>
            <span className="text-xs text-gray-500">(need updates)</span>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <span className="text-red-600 font-bold text-lg">❌ {diffData.diff_summary.deleted_steps}</span>
              <span className="text-gray-700">Deleted Steps</span>
            </div>
            <span className="text-xs text-gray-500">(orphaned SOP sections)</span>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <span className="text-gray-600 font-bold text-lg">✅ {diffData.diff_summary.unchanged_steps}</span>
              <span className="text-gray-700">Unchanged Steps</span>
            </div>
            <span className="text-xs text-gray-500">(in sync)</span>
          </div>
        </div>

        {/* Recommendations */}
        {diffData.recommendations && diffData.recommendations.length > 0 && (
          <div className="mt-4 pt-3 border-t border-blue-200">
            <h5 className="font-semibold text-blue-900 mb-2 flex items-center text-sm">
              <span className="mr-2">💡</span>
              Recommendations
            </h5>
            <ul className="space-y-1 text-sm text-blue-800">
              {diffData.recommendations.map((rec, idx) => (
                <li key={idx} className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>{rec}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Toggle button for detailed view */}
        {diffData.diff_summary.total_changes > 0 && (
          <button
            onClick={() => setShowAllChanges(!showAllChanges)}
            className="mt-3 w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm font-medium transition-colors"
          >
            {showAllChanges ? 'Hide Detailed Changes' : 'View All Changes'}
          </button>
        )}
      </div>
    );
  };

  const renderChangeItem = (change: ChangeItem) => (
    <div key={`${change.type}-${change.step?.id || Math.random()}`} className={`rounded-lg p-3 border ${getChangeBgColor(change.type)} mb-2`}>
      <div className="flex items-start space-x-2">
        <span className="text-xl flex-shrink-0">{getChangeIcon(change.type)}</span>
        <div className="flex-1 min-w-0">
          {/* Step Info */}
          {change.step && (
            <div>
              <div className="font-medium text-gray-900">
                Step {change.step.step_number}: {change.step.step_title}
              </div>
              {change.step.step_description && (
                <div className="text-sm text-gray-600 mt-1 line-clamp-2">
                  {change.step.step_description}
                </div>
              )}
            </div>
          )}

          {/* SOP Mapping Info (for deleted) */}
          {!change.step && change.sop_mapping && (
            <div>
              <div className="font-medium text-gray-900">
                SOP Section {change.sop_mapping.section_number}: {change.sop_mapping.section_title}
              </div>
            </div>
          )}

          {/* Reason */}
          {change.reason && (
            <div className="text-xs text-gray-600 mt-2 bg-white bg-opacity-50 rounded px-2 py-1">
              {change.reason}
            </div>
          )}

          {/* Diff Details for Modified */}
          {change.diff_details && change.type === 'modified' && (
            <div className="text-xs text-gray-700 mt-2 space-y-1">
              {change.diff_details.title_changed && (
                <div className="flex items-center space-x-1">
                  <span className="font-semibold">📝 Title changed</span>
                </div>
              )}
              {change.diff_details.description_changed && (
                <div className="flex items-center space-x-1">
                  <span className="font-semibold">📄 Description changed</span>
                </div>
              )}
              {change.sop_mapping && (
                <div className="text-gray-500 mt-1">
                  Previous: "{change.sop_mapping.section_title}"
                </div>
              )}
            </div>
          )}

          {/* Metadata */}
          <div className="flex items-center space-x-3 mt-2 text-xs text-gray-500">
            {change.step?.modified_at && (
              <span>Modified {formatDate(change.step.modified_at)}</span>
            )}
            {change.step?.modified_by_name && (
              <span>by {change.step.modified_by_name}</span>
            )}
            {change.sop_mapping?.last_synced_at && change.type === 'modified' && (
              <span>Last synced {formatDate(change.sop_mapping.last_synced_at)}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderChangesSection = () => {
    if (!diffData || !showAllChanges) return null;

    const addedChanges = diffData.changes.filter(c => c.type === 'added');
    const modifiedChanges = diffData.changes.filter(c => c.type === 'modified');
    const deletedChanges = diffData.changes.filter(c => c.type === 'deleted');
    const unchangedChanges = diffData.changes.filter(c => c.type === 'unchanged');

    return (
      <div className="space-y-4">
        {/* Added Steps */}
        {addedChanges.length > 0 && (
          <div>
            <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
              <span className="mr-2">➕</span>
              {getChangeTypeLabel('added')} ({addedChanges.length})
            </h4>
            <div className="space-y-2">
              {addedChanges.map(change => renderChangeItem(change))}
            </div>
          </div>
        )}

        {/* Modified Steps */}
        {modifiedChanges.length > 0 && (
          <div>
            <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
              <span className="mr-2">✏️</span>
              {getChangeTypeLabel('modified')} ({modifiedChanges.length})
            </h4>
            <div className="space-y-2">
              {modifiedChanges.map(change => renderChangeItem(change))}
            </div>
          </div>
        )}

        {/* Deleted Steps */}
        {deletedChanges.length > 0 && (
          <div>
            <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
              <span className="mr-2">❌</span>
              {getChangeTypeLabel('deleted')} ({deletedChanges.length})
            </h4>
            <div className="space-y-2">
              {deletedChanges.map(change => renderChangeItem(change))}
            </div>
          </div>
        )}

        {/* Unchanged Steps (Collapsible) */}
        {unchangedChanges.length > 0 && (
          <div>
            <button
              onClick={() => setShowUnchanged(!showUnchanged)}
              className="font-semibold text-gray-700 mb-2 flex items-center hover:text-gray-900 transition-colors"
            >
              <span className="mr-2">{showUnchanged ? '▼' : '▶'}</span>
              <span className="mr-2">✅</span>
              {getChangeTypeLabel('unchanged')} ({unchangedChanges.length})
            </button>
            {showUnchanged && (
              <div className="space-y-2">
                {unchangedChanges.map(change => renderChangeItem(change))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-6">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading diff...</span>
      </div>
    );
  }

  if (!diffData) {
    return (
      <div className="text-center p-6 text-gray-500">
        No diff data available
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Process Map Info */}
      <div className="bg-white rounded-lg p-3 border border-gray-200">
        <h3 className="font-semibold text-gray-900 mb-1">
          {diffData.process_map.process_name}
        </h3>
        <div className="text-sm text-gray-600 flex items-center space-x-4">
          <span>Level {diffData.process_map.level}</span>
          <span>•</span>
          <span>{diffData.process_map.total_steps} steps</span>
          {diffData.sop_info.total_sops > 0 && (
            <>
              <span>•</span>
              <span>{diffData.sop_info.total_sops} SOP(s) linked</span>
            </>
          )}
        </div>
      </div>

      {/* Diff Summary */}
      {renderDiffSummary()}

      {/* Detailed Changes */}
      {renderChangesSection()}
    </div>
  );
};

export default ProcessMapDiff;
